import type { FlowchartHint, WorkflowPlan } from "./types";

export interface ProjectedNode {
  id: string;
  label: string;
  kind: string;
  group?: string;
  x?: number;
  y?: number;
  mobileLabel: string;
}

export interface ProjectedEdge {
  id: string;
  from: string;
  to?: string;
  label?: string;
  escalate: boolean;
}

/** Flowchart is a projection of WorkflowPlan; only display hints are stored. */
export function projectFlowchart(plan: WorkflowPlan, hints: FlowchartHint[] = []) {
  const hintMap = new Map(hints.map((hint) => [hint.step_id, hint]));
  const nodes: ProjectedNode[] = plan.steps.map((step) => {
    const hint = hintMap.get(step.step_id);
    return {
      id: step.step_id,
      label: hint?.label ?? step.kind,
      kind: step.kind,
      group: hint?.group,
      x: hint?.x,
      y: hint?.y,
      mobileLabel: hint?.label ?? step.kind,
    };
  });
  const edges: ProjectedEdge[] = [];
  plan.steps.forEach((step) => {
    for (const dependency of step.depends_on ?? []) {
      edges.push({ id: `${dependency}->${step.step_id}`, from: dependency, to: step.step_id, escalate: false });
    }
    if (step.kind === "branch") {
      for (const [index, branchCase] of (step.cases ?? []).entries()) {
        edges.push({
          id: `${step.step_id}->case-${index}`,
          from: step.step_id,
          to: branchCase.next,
          label: describePredicate(branchCase.when),
          escalate: false,
        });
      }
      if (step.default?.type === "next") {
        edges.push({ id: `${step.step_id}->default`, from: step.step_id, to: step.default.step_id, label: "default", escalate: false });
      } else {
        edges.push({ id: `${step.step_id}->escalate`, from: step.step_id, label: "escalate", escalate: true });
      }
    }
    if (step.kind === "loop") {
      edges.push({ id: `${step.step_id}->loop`, from: step.step_id, to: step.body_entry, label: "loop", escalate: false });
    }
  });
  return { nodes, edges };
}

export function describePredicate(predicate: { op: string; var?: string; value?: unknown }): string {
  switch (predicate.op) {
    case "exists":
      return `${predicate.var} is present`;
    case "eq":
      return `${predicate.var} is ${String(predicate.value)}`;
    case "in":
      return `${predicate.var} is one of the listed values`;
    default:
      return predicate.op;
  }
}
